<template>
  <div class="metronome">
    <div class="metronome-header">
      <van-button round plain size="small" @click="showLanguageModal = true" class="language-button">
        {{ currentLanguageName }}
      </van-button>
    </div>

    <div class="metronome-content">
      <div class="tempo-section">
        <TempoControl v-model:tempo="tempo" :bpm-text="t('metronome.tempo.bpm')" @adjust="adjustTempo"
          @commit="reschedule" />
      </div>

      <div class="animation-section">
        <PendulumAnimation :beats-count="currentSignature.beats" :current-beat="currentBeat"
          :is-swinging="isPlaying" :active="isActive" :get-timeline="getTimeline"
          :beat-interval="beatInterval" />
      </div>

      <div class="controls-section">
        <van-row type="flex" justify="space-around" align="center" gutter="16">
          <van-col>
            <van-button type="primary" size="large" @click="showPresetsModal = true" class="control-button">
              {{ currentPresetName }}
            </van-button>
          </van-col>
          <van-col>
            <van-button color="#07c160" size="large" @click="tapTempo" class="control-button tap-button">
              {{ t('metronome.tempo.tap') }}
            </van-button>
          </van-col>
          <van-col>
            <van-button type="primary" size="large" @click="showTimeSignatureModal = true" class="control-button">
              {{ currentSignature.label }}
            </van-button>
          </van-col>
        </van-row>

        <van-button :color="isPlaying ? '#ee0a24' : '#07c160'" size="large" block class="play-button"
          @click="togglePlay">
          <template #icon>
            <van-icon :name="isPlaying ? 'stop-circle-o' : 'play-circle-o'" />
          </template>
          {{ isPlaying ? t('metronome.controls.stop') : t('metronome.controls.start') }}
        </van-button>

        <!-- 移动端音频被浏览器策略挡住时给出手动排查提示，避免用户以为程序坏了 -->
        <p v-if="audioBlocked" class="audio-hint">{{ t('metronome.audio.hint') }}</p>

        <!-- 部分手机内核给 Web Audio 单独上了低增益，改走系统媒体通道能明显更响 -->
        <div v-if="showOutputToggle" class="output-row">
          <span class="output-label">{{ t('metronome.audio.output') }}</span>
          <van-switch :model-value="outputMode === 'media'" size="20" @update:model-value="toggleOutputMode" />
        </div>
        <p v-if="showOutputToggle" class="audio-hint">{{ t('metronome.audio.outputHint') }}</p>
      </div>
    </div>

    <!-- 语言选择弹窗 -->
    <van-dialog v-model:show="showLanguageModal" :title="t('language.title')" class="language-dialog">
      <van-cell-group>
        <van-cell v-for="lang in languages" :key="lang.code" :title="lang.name" is-link clickable
          @click="selectLanguage(lang.code)" />
      </van-cell-group>
    </van-dialog>

    <!-- 预设速度弹窗 -->
    <TempoPresets v-model:show="showPresetsModal" :title="t('metronome.presets.title')" :current-tempo="tempo"
      :presets="tempoPresets" @select="selectPreset" />

    <!-- 拍号选择弹窗 -->
    <TimeSignature v-model:show="showTimeSignatureModal" :title="t('metronome.timeSignature.title')"
      :current-id="currentSignature.id" :signatures="timeSignatures" @select="selectTimeSignature" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { setLocale, type AppLocale } from '@/locales'
import { MIN_TEMPO, MAX_TEMPO } from '@/constants/tempo'
import { useMetronome } from '@/composables/useMetronome'
import { isMobileBrowser } from '@/components/sounds/outputMode'
import PendulumAnimation from './animations/PendulumAnimation.vue'
import TempoControl from './controls/TempoControl.vue'
import TempoPresets from './controls/TempoPresets.vue'
import TimeSignature from './controls/TimeSignature.vue'
import type { TimeSignatureOption } from './controls/types'

/** Tap Tempo 超时重置阈值（毫秒） */
const TAP_TIMEOUT_MS = 2000

const { t, locale } = useI18n()
const showLanguageModal = ref(false)
const showPresetsModal = ref(false)
const showTimeSignatureModal = ref(false)

const languages: { code: AppLocale; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'zh', name: '中文' }
]

const currentLanguageName = computed(
  () => languages.find((lang) => lang.code === locale.value)?.name || 'English',
)

function selectLanguage(code: AppLocale) {
  setLocale(code)
  showLanguageModal.value = false
}

const tempo = ref(120)
const beatsPerBar = ref(4)
const currentSignature = ref<TimeSignatureOption>({
  id: '4/4',
  beats: 4,
  unit: 4,
  label: '4/4',
  icon: '♩♩♩♩',
})

const { isPlaying, isActive, currentBeat, audioBlocked, outputMode, toggle, reschedule, getTimeline, setOutputMode } =
  useMetronome(tempo, { beatsPerBar })

// 桌面端两条通路电平一致，这个开关只对手机有意义
const showOutputToggle = isMobileBrowser()

function toggleOutputMode(on: boolean) {
  void setOutputMode(on ? 'media' : 'webaudio')
}

const tempoPresets: Record<string, { value: number }> = {
  grave: { value: 40 },
  largo: { value: 50 },
  adagio: { value: 66 },
  andante: { value: 76 },
  moderato: { value: 98 },
  allegro: { value: 120 },
  vivace: { value: 152 },
  presto: { value: 176 }
}

const currentPresetName = computed(() => {
  const preset = Object.entries(tempoPresets).find(([, p]) => p.value === tempo.value)
  return preset ? t(`metronome.tempo.presets.${preset[0]}`) : `${tempo.value} BPM`
})

const timeSignatures: TimeSignatureOption[] = [
  { id: '1/4', beats: 1, unit: 4, label: '1/4', icon: '♩' },
  { id: '2/4', beats: 2, unit: 4, label: '2/4', icon: '♩♩' },
  { id: '3/4', beats: 3, unit: 4, label: '3/4', icon: '♩♩♩' },
  { id: '4/4', beats: 4, unit: 4, label: '4/4', icon: '♩♩♩♩' },
  { id: '3/8', beats: 3, unit: 8, label: '3/8', icon: '♪♪♪' },
  { id: '6/8', beats: 6, unit: 8, label: '6/8', icon: '♪♪♪♪♪♪' }
]

// 无 Web Audio 时供动画回退相位钟使用的一拍时长（秒）
const beatInterval = computed(() => 60 / tempo.value)

let tapTimes: number[] = []
let lastTapTime = 0

function togglePlay() {
  void toggle()
}

function selectPreset(value: number) {
  tempo.value = value
  // 弹窗由 TempoPresets 内部关闭
}

function selectTimeSignature(sig: TimeSignatureOption) {
  currentSignature.value = sig
  beatsPerBar.value = sig.beats
}

function tapTempo() {
  const now = Date.now()
  // 距上次点击超时则重新开始计数，避免陈旧间隔拉低速度
  if (now - lastTapTime > TAP_TIMEOUT_MS) {
    tapTimes = []
  }
  lastTapTime = now
  tapTimes.push(now)

  // 只保留最近的 4 次点击
  if (tapTimes.length > 4) {
    tapTimes.shift()
  }

  if (tapTimes.length >= 2) {
    let total = 0
    for (let i = 1; i < tapTimes.length; i++) {
      total += tapTimes[i]! - tapTimes[i - 1]!
    }
    const averageInterval = total / (tapTimes.length - 1)
    const newTempo = Math.round(60000 / averageInterval)
    if (newTempo >= MIN_TEMPO && newTempo <= MAX_TEMPO) {
      tempo.value = newTempo
    }
  }
}

function adjustTempo(change: number) {
  const newValue = tempo.value + change
  if (newValue >= MIN_TEMPO && newValue <= MAX_TEMPO) {
    tempo.value = newValue
  }
}

function handleKeydown(e: KeyboardEvent) {
  if (e.code === 'Space') {
    // 焦点在按钮/输入元素上时交给原生行为，避免双重触发
    const target = e.target as HTMLElement | null
    if (target && (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.isContentEditable)) {
      return
    }
    e.preventDefault()
    togglePlay()
  } else if (e.code === 'ArrowLeft') {
    adjustTempo(-1)
  } else if (e.code === 'ArrowRight') {
    adjustTempo(1)
  } else if (e.code === 'KeyT') {
    const target = e.target as HTMLElement | null
    if (target && (target.tagName === 'INPUT' || target.isContentEditable)) return
    tapTempo()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.metronome {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100vh;
  height: 100dvh; /* 避免移动端地址栏导致的布局跳动 */
  padding: 16px;
  background: var(--van-background);
}

.metronome-header {
  display: flex;
  justify-content: flex-end;
  padding: 8px;
}

.language-button {
  width: auto;
  height: auto;
  padding: 0 14px;
  font-size: 14px;
}

.metronome-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--app-shadow);
}

.tempo-section {
  padding: 16px 0;
}

.animation-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px 0;
  min-height: 160px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.02);
  overflow: hidden;
}

.controls-section {
  padding: 16px 0 8px;
}

.control-button {
  width: 100%;
  min-width: 90px;
  height: 44px;
  font-size: 15px;
}

.tap-button {
  position: relative;
  overflow: hidden;
}

.tap-button::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: width 0.3s, height 0.3s;
}

.tap-button:active::after {
  width: 120px;
  height: 120px;
}

.play-button {
  margin: 24px 0 0;
  height: 56px;
  font-size: 18px;
  border-radius: 16px;
}

.play-button :deep(.van-icon) {
  font-size: 24px;
  margin-right: 8px;
  vertical-align: middle;
}

.audio-hint {
  margin: 10px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--van-text-color-2);
}

.output-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 14px 0 0;
}

.output-label {
  font-size: 14px;
  color: var(--van-text-color);
}

/* 语言选择弹窗 */
:deep(.language-dialog .van-dialog__header) {
  padding: 16px;
  font-weight: 600;
}

:deep(.language-dialog .van-cell) {
  font-size: 16px;
  padding: 16px;
}

/* 适配移动设备 */
@media (max-width: 767px) {
  .metronome {
    padding: 8px;
  }

  .metronome-content {
    padding: 16px;
  }

  .animation-section {
    min-height: 140px;
  }

  .control-button {
    height: 40px;
    font-size: 14px;
  }

  .play-button {
    height: 48px;
    font-size: 16px;
    margin: 16px 0 0;
  }
}

/* 适配超小屏幕 */
@media (max-width: 320px) {
  .metronome-content {
    padding: 12px;
  }

  :deep(.van-row) {
    flex-direction: column;
  }

  :deep(.van-col) {
    width: 100%;
    margin-bottom: 8px;
  }

  .control-button {
    width: 100%;
  }

  .animation-section {
    min-height: 120px;
  }
}

/* 暗色模式适配 */
@media (prefers-color-scheme: dark) {
  .metronome-content {
    background: rgba(28, 28, 30, 0.95);
  }

  .animation-section {
    background: rgba(255, 255, 255, 0.03);
  }
}
</style>
